import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from './Seo';
import Breadcrumbs, { buildBreadcrumbSchema } from './Breadcrumbs';
import './App.css';
import './Safety.css';
import Footer from './Footer';
import Navbar from './Navbar';

const breadcrumbItems = [
  { name: 'Home', path: '/' },
  { name: 'Safety', path: '/safety' },
];

function Safety() {
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="Safety & Trust on Baatcheet — Verification, Encryption & Reporting | Baatcheet"
        description="Learn how Baatcheet keeps you safe — identity verification, end-to-end encrypted calls, instant block & report tools, and 24/7 moderation."
        path="/safety"
        jsonLd={buildBreadcrumbSchema(breadcrumbItems)}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" /><i className="cbar s2" /><i className="dot s3" /><i className="cbar s4" />
        <i className="dot s5" /><i className="dot s6" /><i className="cbar s7" /><i className="dot s8" />
        <i className="cbar s9" /><i className="dot s10" />
      </div>

      {/* ── Header ── */}
      <Navbar />
      <Breadcrumbs items={breadcrumbItems} />

      <main>

        {/* ── HERO ── */}
        <section className="sf-hero shell">
          <div className="sf-hero-inner">
            <div className="sf-hero-char sf-hero-char--left reveal">
              <img src="/safety-girl.webp" alt="Baatcheet user protected by identity verification" width="764" height="1024" fetchpriority="high" />
            </div>

            <div className="sf-hero-copy reveal">
              <div className="sf-eyebrow">
                <span className="sf-shield-dot" />
                Baatcheet Shield
              </div>
              <h1 className="sf-title">
                Built Safe.<br />
                <span className="sf-title-stamp">Kept Safe.</span>
              </h1>
              <p className="sf-sub">
                We designed Baatcheet from the ground up so that trust is not a feature — it is the foundation. Every conversation happens inside a three-layer protection system that we call the Baatcheet Shield. See <Link to="/how-it-works">how a call actually works</Link> once you're matched.
              </p>
              <a className="btn btn-cyan" href="mailto:baatcheet.connect@gmail.com?subject=Safety%20Concern">Talk to Safety Team →</a>
            </div>

            <div className="sf-hero-char sf-hero-char--right reveal">
              <img src="/safety-boy.webp" alt="Baatcheet user protected by end-to-end encryption" width="715" height="1024" loading="lazy" decoding="async" />
            </div>
          </div>

          {/* Trust stats row */}
          <div className="sf-stats reveal">
            <div className="sf-stat">
              <span className="sf-stat-num">100%</span>
              <span className="sf-stat-label">Verified accounts</span>
            </div>
            <div className="sf-stat-divider" />
            <div className="sf-stat">
              <span className="sf-stat-num">E2E</span>
              <span className="sf-stat-label">Encrypted calls &amp; chats</span>
            </div>
            <div className="sf-stat-divider" />
            <div className="sf-stat">
              <span className="sf-stat-num">&lt; 24h</span>
              <span className="sf-stat-label">Report response time</span>
            </div>
            <div className="sf-stat-divider" />
            <div className="sf-stat">
              <span className="sf-stat-num">24 / 7</span>
              <span className="sf-stat-label">Human moderation</span>
            </div>
          </div>
        </section>

        {/* ── LAYER 01: IDENTITY ── */}
        <section className="sf-layer shell">
          <div className="sf-layer-grid reveal">
            <div className="sf-layer-copy">
              <div className="sf-layer-badge sf-badge--teal">Layer 01</div>
              <h2 className="sf-layer-title">
                Every face<br />
                <span className="sf-script">checked twice.</span>
              </h2>
              <p className="sf-layer-desc">
                Before anyone can send a single message on Baatcheet, they go through our Identity Gate — a real-person check that combines government ID with a live selfie match. No shortcuts, no bypasses.
              </p>
              <ul className="sf-checklist">
                <li><span className="sf-check sf-check--teal">✓</span> Government photo ID verification</li>
                <li><span className="sf-check sf-check--teal">✓</span> Live selfie matched against ID</li>
                <li><span className="sf-check sf-check--teal">✓</span> Re-verification if behaviour flags arise</li>
                <li><span className="sf-check sf-check--teal">✓</span> 24 / 7 AI + human review layer</li>
              </ul>
            </div>
            <div className="sf-layer-visual sf-visual--right">
              <div className="sf-img-card">
                <img src="/safety-verify.webp" alt="Identity verification process" width="1536" height="1024" loading="lazy" decoding="async" />
                <div className="sf-img-label">Identity Gate</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── LAYER 02: PRIVACY ── */}
        <section className="sf-layer sf-layer--alt shell">
          <div className="sf-layer-grid sf-layer-grid--flip reveal">
            <div className="sf-layer-visual sf-visual--left">
              <div className="sf-img-card sf-img-card--rotate">
                <img src="/safety-privacy.webp" alt="End-to-end encryption" width="1536" height="1024" loading="lazy" decoding="async" />
                <div className="sf-img-label">Privacy Vault</div>
              </div>
            </div>
            <div className="sf-layer-copy">
              <div className="sf-layer-badge sf-badge--indigo">Layer 02</div>
              <h2 className="sf-layer-title">
                Your words leave<br />
                <span className="sf-script">no footprint.</span>
              </h2>
              <p className="sf-layer-desc">
                Everything said inside Baatcheet is encrypted end-to-end. We cannot read it. We do not store it. No admin, no algorithm, and no third party ever touches your conversation — see how this powers our <Link to="/anonymous-chat-app">anonymous chat</Link> experience.
              </p>
              <ul className="sf-checklist">
                <li><span className="sf-check sf-check--indigo">✓</span> End-to-end encrypted audio &amp; video</li>
                <li><span className="sf-check sf-check--indigo">✓</span> Zero server-side call recordings</li>
                <li><span className="sf-check sf-check--indigo">✓</span> No admin access to message content</li>
                <li><span className="sf-check sf-check--indigo">✓</span> Session data auto-cleared after call</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── LAYER 03: CONTROL ── */}
        <section className="sf-layer shell">
          <div className="sf-layer-grid reveal">
            <div className="sf-layer-copy">
              <div className="sf-layer-badge sf-badge--rose">Layer 03</div>
              <h2 className="sf-layer-title">
                One tap and<br />
                <span className="sf-script">they're gone.</span>
              </h2>
              <p className="sf-layer-desc">
                You are always the one in control. If a conversation turns uncomfortable — even slightly — you have instant tools to act. Our team receives every report within seconds and acts fast.
              </p>
              <ul className="sf-checklist">
                <li><span className="sf-check sf-check--rose">✓</span> Instant one-tap block — no confirmation</li>
                <li><span className="sf-check sf-check--rose">✓</span> Pinned report button on every profile</li>
                <li><span className="sf-check sf-check--rose">✓</span> Under-24h review with real consequences</li>
                <li><span className="sf-check sf-check--rose">✓</span> Strict no hate speech &amp; no harassment policy</li>
              </ul>
            </div>
            <div className="sf-layer-visual sf-visual--right">
              <div className="sf-img-card">
                <img src="/safety-report.webp" alt="Report and block tools" width="1536" height="1024" loading="lazy" decoding="async" />
                <div className="sf-img-label">Control Layer</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TRUST RECEIPT ── */}
        <section className="sf-receipt-wrap shell reveal">
          <div className="sf-receipt">
            <div className="sf-receipt-header">
              <span className="sf-receipt-logo">
                <img src="/logo.webp" alt="Baatcheet" width="512" height="512" loading="lazy" decoding="async" />
              </span>
              <span className="sf-receipt-title">Baatcheet Safety Promise</span>
              <span className="sf-receipt-tag">Always Active</span>
            </div>
            <div className="sf-receipt-rows">
              <div className="sf-receipt-row">
                <span>Identity verified</span><span className="sf-receipt-val">✓ Done before first message</span>
              </div>
              <div className="sf-receipt-row">
                <span>Calls recorded by us</span><span className="sf-receipt-val sf-val--red">✗ Never</span>
              </div>
              <div className="sf-receipt-row">
                <span>Admin can read your chats</span><span className="sf-receipt-val sf-val--red">✗ Impossible</span>
              </div>
              <div className="sf-receipt-row">
                <span>Block takes effect</span><span className="sf-receipt-val">✓ Instantly</span>
              </div>
              <div className="sf-receipt-row">
                <span>Reports reviewed by humans</span><span className="sf-receipt-val">✓ Within 24 hours</span>
              </div>
              <div className="sf-receipt-row">
                <span>Your data sold to third parties</span><span className="sf-receipt-val sf-val--red">✗ Never, ever</span>
              </div>
            </div>
            <div className="sf-receipt-footer">This promise applies to every account, every call, every day.</div>
          </div>
        </section>

        {/* ── CTA BAND ── */}
        <section className="sf-cta-band reveal">
          <div className="sf-cta-inner shell">
            <div className="sf-cta-copy">
              <h2 className="sf-cta-title">Need help or<br />have a concern?</h2>
              <p className="sf-cta-sub">
                Our dedicated Safety Team is here for you — every day, any time. Reach out directly and we will respond within 24 hours. You can also read our full policies below.
              </p>
              <div className="sf-cta-btns">
                <a
                  className="btn btn-cyan"
                  href="mailto:baatcheet.connect@gmail.com?subject=Safety%20Concern"
                >
                  ✉ Contact Safety Team
                </a>
                <Link className="btn sf-policy-btn" to="/privacy-policy">
                  Privacy &amp; Policies →
                </Link>
              </div>
            </div>
            <div className="sf-cta-img">
              <img src="/safety-community.webp" alt="Baatcheet community" width="1536" height="1024" loading="lazy" decoding="async" />
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </>
  );
}

export default Safety;
